import { productUnitDescriptor } from '../descriptors/product-unit.descriptor.js';
import { canDeriveProductUnitFactor, deriveProductUnitFactorFromProduct, } from '../services/deriveProductUnitFactor.js';
import { lifecycleService } from '../services/lifecycleService.js';
import { effectiveProductUnitKind, isProductUnitDerived, isProductUnitPackaging, } from '../services/productUnitKind.js';
import { MAX_QUANTITY_DECIMALS } from '../validation/fieldLimits.js';
import { roundQuantity } from '../validation/quantity.js';
import { mergeErrors } from './mergeErrors.js';
import { integer, max, nonNegative, positive } from './rules.js';
import { runDescriptorValidation } from './runDescriptorValidation.js';
import { validateWithRules } from './validateWithRules.js';
/**
 * L0 + L1 validation for ProductUnit (0118 / request 0283).
 *
 * `ctx.extra`:
 * - `siblings` — other product units for the same product (isDefault / codes)
 * - `product` — owning product (quantityDecimals for packaging factor; derivation attrs)
 * - `excludeId` — id being updated
 */
export function validateProductUnit(entity, ctx) {
    const extras = (ctx?.extra ?? {});
    const kind = effectiveProductUnitKind(entity);
    const isMeasure = kind === 'measure';
    const isPackaging = kind === 'packaging';
    const base = mergeErrors(runDescriptorValidation(productUnitDescriptor, entity, [
        'productId',
        'description',
        'factor',
    ]), validateWithRules(entity, {
        factor: [positive('Fator deve ser maior que zero.')],
        quantityDecimals: [
            integer('Informe um número inteiro de casas decimais.'),
            nonNegative('Não pode ser negativo.'),
            max(MAX_QUANTITY_DECIMALS, 'Máximo de 4 casas decimais.'),
        ],
    }, ctx));
    const errors = { ...(base ?? {}) };
    if (isMeasure) {
        const unitCode = (entity.unitCode ?? '').trim();
        if (!unitCode) {
            errors['unitCode'] = 'Informe o código da unidade de medida.';
        }
        if ((entity.barcode ?? '').trim()) {
            errors['barcode'] = 'Código de barras é só para embalagem.';
        }
        if ((entity.standardPackagingId ?? '').trim()) {
            errors['standardPackagingId'] = 'Modelo de embalagem é só para embalagem.';
        }
        if (entity.isDefault === true) {
            errors['isDefault'] = 'Unidade padrão é só para embalagem.';
        }
    }
    if (entity.isDerived === true && !isMeasure) {
        errors['isDerived'] = 'Cálculo pelo cadastro do produto é só para medida.';
    }
    // Packaging factor respects Product.quantityDecimals; measure does not (0283).
    if (isPackaging &&
        typeof entity.factor === 'number' &&
        Number.isFinite(entity.factor) &&
        entity.factor > 0) {
        const decimals = typeof extras.product?.quantityDecimals === 'number' ? extras.product.quantityDecimals : 0;
        const rounded = roundQuantity(entity.factor, decimals);
        if (rounded !== entity.factor) {
            errors['factor'] =
                `Fator deve respeitar ${decimals} casa(s) decimal(is) do produto (ex.: ${rounded}).`;
        }
    }
    if (isProductUnitDerived(entity)) {
        const product = extras.product ?? null;
        if (!product) {
            errors['isDerived'] = 'Carregue o produto para calcular o fator.';
        }
        else if (!canDeriveProductUnitFactor(product.unit, entity.unitCode)) {
            errors['isDerived'] =
                'Esta combinação de unidade-base do produto e código da linha não tem fórmula. Desmarque o cálculo ou use um código reconhecido (KG, M, M2, UN).';
        }
        else {
            const derived = deriveProductUnitFactorFromProduct(product, entity.unitCode);
            if (!derived.ok) {
                if (derived.reason === 'missing-attr' && derived.missingLabel) {
                    errors['isDerived'] =
                        `Para calcular esta unidade, preencha ${derived.missingLabel} no cadastro do produto (maior que zero).`;
                }
                else {
                    errors['isDerived'] = 'Não foi possível calcular o fator a partir do produto.';
                }
            }
        }
    }
    if (entity.isDefault && isPackaging && lifecycleService.isActive(entity)) {
        const siblings = extras.siblings ?? [];
        const excludeId = extras.excludeId ?? entity.id;
        const conflict = siblings.find((s) => s.id !== excludeId &&
            s.isDefault === true &&
            isProductUnitPackaging(s) &&
            lifecycleService.isActive(s) &&
            s.productId === (entity.productId ?? '').trim());
        if (conflict) {
            errors['isDefault'] =
                `Já existe unidade padrão ativa ("${conflict.description}"). Desmarque a outra antes.`;
        }
    }
    const barcode = entity.barcode?.trim();
    if (barcode) {
        const siblings = extras.siblings ?? [];
        const excludeId = extras.excludeId ?? entity.id;
        const dup = siblings.find((s) => s.id !== excludeId &&
            (s.barcode ?? '').trim() === barcode &&
            lifecycleService.isActive(s));
        if (dup) {
            errors['barcode'] = `Código de barras já usado na unidade "${dup.description}".`;
        }
    }
    const unitCode = entity.unitCode?.trim().toUpperCase();
    if (unitCode && lifecycleService.isActive(entity)) {
        const siblings = extras.siblings ?? [];
        const excludeId = extras.excludeId ?? entity.id;
        const myKind = kind;
        const dup = siblings.find((s) => s.id !== excludeId &&
            s.productId === (entity.productId ?? '').trim() &&
            (s.unitCode ?? '').trim().toUpperCase() === unitCode &&
            lifecycleService.isActive(s));
        if (dup) {
            const theirKind = effectiveProductUnitKind(dup);
            // D9: packaging may repeat packaging code; measure is unique; packaging↔measure cannot share.
            if (myKind === 'measure' || theirKind === 'measure') {
                if (myKind === 'measure' && theirKind === 'measure') {
                    errors['unitCode'] = `Código de medida já usado em "${dup.description}".`;
                }
                else {
                    errors['unitCode'] =
                        `Código já usado como ${theirKind === 'measure' ? 'medida' : 'embalagem'} em "${dup.description}". Embalagem e medida não compartilham o mesmo código.`;
                }
            }
        }
    }
    return Object.keys(errors).length > 0 ? errors : undefined;
}
//# sourceMappingURL=validateProductUnit.js.map